import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  Injector,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  DEFAULT_SELECTION_CONFIG,
  DEFAULT_SORT,
  alignClass,
  isSelectable,
  resolveColumnAlign,
  type ColumnAlign,
  type ColumnWidths,
  type DocumentItem,
  type SortState,
} from '@document-explorer/core';
import { DEFAULT_INTERACTION, type ActionDef, type ColumnDef, type InteractionConfig, type ItemTemplate } from './types.js';
import { DEFAULT_COLUMNS, gridMinimum, gridTemplate, narrowGridTemplate } from './columns.js';
import { DeIconComponent } from './icon.component.js';
import { DeUiIconComponent } from './ui-icon.component.js';
import { DeActionsMenuComponent } from './actions-menu.component.js';
import { DeCellValuePipe } from './cell-value.pipe.js';
import { DeColumnResizerComponent } from './column-resizer.component.js';
import type { DeColumnTemplateDirective } from './column-template.directive.js';
import { RovingFocus } from './roving-focus.js';
import type { SelectionConfig } from '@document-explorer/core';

@Component({
  selector: 'de-list-view',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    DeIconComponent,
    DeUiIconComponent,
    DeActionsMenuComponent,
    DeCellValuePipe,
    DeColumnResizerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      #container
      class="de-list"
      role="grid"
      (focusin)="roving.noteFocusIn()"
      (focusout)="roving.noteFocusOut($event)"
      [attr.aria-rowcount]="items().length + 1"
      [style.--de-grid-columns]="tracks()"
      [style.--de-grid-columns-narrow]="narrowTracks()"
      [style.--de-grid-min-width]="minWidth()"
    >
      <div class="de-list-head" role="row">
        @if (selectionEnabled()) {
          <span role="columnheader"><span class="de-sr-only">Select</span></span>
        }
        @for (column of columns(); track column.key) {
          <div
            role="columnheader"
            class="de-cell"
            [class]="'de-cell ' + alignFor(column)"
            [class.de-cell--optional]="column.optional !== false"
            [class.de-cell--resizable]="canResize(column)"
            [attr.aria-sort]="ariaSort(column)"
            [attr.data-de-column]="column.key"
          >
            <button
              type="button"
              [class]="'de-col-header ' + alignFor(column)"
              [class.de-col-header--sortable]="column.sortable"
              [disabled]="!column.sortable"
              (click)="column.sortable && sortChange.emit(column.key)"
            >
              {{ column.label }}
              @if (sort().key === column.key) {
                <span class="de-sort-indicator" aria-hidden="true">
                  <de-ui-icon [name]="sort().direction === 'asc' ? 'chevronUp' : 'chevronDown'" />
                </span>
              }
            </button>

            @if (canResize(column)) {
              <de-column-resizer
                [label]="column.label"
                [minWidth]="column.minWidth"
                [maxWidth]="column.maxWidth"
                [currentWidth]="columnWidths()[column.key]"
                [measure]="measurerFor(column.key)"
                (resized)="columnResize.emit({ key: column.key, width: $event })"
                (committed)="columnResizeCommit.emit(column.key)"
                (reset)="columnResizeReset.emit(column.key)"
              />
            }
          </div>
        }
        @if (hasActions()) {
          <span role="columnheader"><span class="de-sr-only">Actions</span></span>
        }
      </div>

      @for (item of items(); track item.id; let index = $index) {
        <div
          role="row"
          class="de-row"
          [class.de-row--folder]="item.type === 'folder'"
          [class.de-row--file]="item.type === 'file'"
          [attr.aria-selected]="selectionEnabled() ? selectedIds().has(item.id) : null"
          [attr.aria-rowindex]="index + 2"
          [attr.data-de-index]="index"
          [attr.tabindex]="roving.tabIndexFor(index)"
          (keydown)="onKeyDown($event)"
          (focus)="roving.setActive(index)"
          (click)="onClick(item, $event)"
          (dblclick)="onDoubleClick(item)"
        >
          @if (selectionEnabled()) {
            <!-- Inside a gridcell: a row only allows cell children. -->
            <div role="gridcell">
              <input
                type="checkbox"
                class="de-checkbox"
                [checked]="selectedIds().has(item.id)"
                [disabled]="!canSelect(item)"
                [attr.aria-label]="'Select ' + item.name"
                (click)="onCheckboxClick($event, item)"
              />
            </div>
          }

          @if (templateFor(item); as template) {
            <div class="de-cell" role="gridcell">
              <ng-container *ngTemplateOutlet="template; context: { $implicit: item, item: item }" />
            </div>
          } @else {
            @for (column of columns(); track column.key) {
              <div
                role="gridcell"
                [class]="'de-cell ' + alignFor(column)"
                [class.de-name-cell]="column.key === 'name'"
                [class.de-cell--numeric]="column.numeric"
                [class.de-cell--muted]="column.key !== 'name'"
                [class.de-cell--optional]="column.optional !== false"
                [class.de-cell--custom]="!!columnTemplate(column.key)"
              >
                @if (columnTemplate(column.key); as cellTemplate) {
                  <!-- Consumer-supplied cell: an avatar, a pill, a badge. -->
                  <ng-container
                    *ngTemplateOutlet="
                      cellTemplate;
                      context: {
                        \$implicit: item,
                        item: item,
                        value: (item | deCellValue: column : locale())
                      }
                    "
                  />
                } @else if (column.key === 'name') {
                  <de-icon [item]="item" />
                  <span class="de-name">{{ item | deCellValue: column : locale() }}</span>
                } @else {
                  {{ item | deCellValue: column : locale() }}
                }
              </div>
            }
          }

          @if (hasActions()) {
            <div role="gridcell">
              <de-actions-menu [item]="item" [actions]="actions()" (action)="action.emit($event)" />
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class DeListViewComponent {
  readonly items = input<DocumentItem[]>([]);
  readonly columns = input<ColumnDef[]>(DEFAULT_COLUMNS);
  readonly sort = input<SortState>(DEFAULT_SORT);
  readonly actions = input<ActionDef[]>([]);
  readonly interaction = input<Required<InteractionConfig>>(DEFAULT_INTERACTION);
  readonly selectionConfig = input<SelectionConfig>(DEFAULT_SELECTION_CONFIG);
  readonly selectedIds = input<ReadonlySet<string>>(new Set<string>());
  readonly locale = input<string | undefined>(undefined);
  readonly fileTemplate = input<ItemTemplate | undefined>(undefined);
  readonly folderTemplate = input<ItemTemplate | undefined>(undefined);
  readonly columnTemplates = input<readonly DeColumnTemplateDirective[]>([]);
  readonly resizable = input<boolean>(false);
  readonly columnWidths = input<ColumnWidths>({});
  readonly columnAlign = input<ColumnAlign | undefined>(undefined);

  readonly sortChange = output<string>();
  readonly open = output<DocumentItem>();
  readonly toggleSelect = output<DocumentItem>();
  readonly selectRange = output<DocumentItem>();
  readonly selectAll = output<void>();
  readonly clearSelection = output<void>();
  readonly navigateUp = output<void>();
  readonly action = output<{ actionId: string; item: DocumentItem }>();
  readonly columnResize = output<{ key: string; width: number }>();
  readonly columnResizeCommit = output<string>();
  readonly columnResizeReset = output<string>();

  private readonly container = viewChild<ElementRef<HTMLElement>>('container');
  protected readonly roving = new RovingFocus(() => this.container(), inject(Injector));

  protected readonly selectionEnabled = computed(() => this.selectionConfig().enabled);
  protected readonly hasActions = computed(() => this.actions().length > 0);
  protected readonly tracks = computed(() =>
    gridTemplate(this.columns(), this.selectionEnabled(), this.hasActions(), this.columnWidths()),
  );

  // Header cells are measured live, so a drag starts from the width actually on
  // screen — including a flexible track that has never been given a pixel value.
  private readonly measurers = new Map<string, () => number>();
  protected readonly narrowTracks = computed(() =>
    narrowGridTemplate(this.selectionEnabled(), this.hasActions()),
  );
  protected readonly minWidth = computed(() =>
    gridMinimum(this.columns(), this.selectionEnabled(), this.hasActions(), this.columnWidths()),
  );

  /**
   * Identify the listing by its contents, not by array identity. A consumer
   * binding `[data]="docs | filterSomehow"` hands us a fresh array on every
   * change-detection pass, and reacting to identity alone would drag the user
   * back to the first row between one keystroke and the next.
   */
  private readonly listSignature = computed(() => {
    const items = this.items();
    return `${items.length}:${items[0]?.id ?? ''}:${items[items.length - 1]?.id ?? ''}`;
  });

  constructor() {
    effect(() => {
      this.listSignature();
      this.roving.syncToList();
    });
  }

  protected canSelect(item: DocumentItem): boolean {
    return isSelectable(item, this.selectionConfig());
  }

  /**
   * The alignment class for a column, used on both the header and its cells so
   * the label and the values always sit on the same edge.
   */
  protected alignFor(column: ColumnDef): string {
    return alignClass(resolveColumnAlign(column.align, this.columnAlign()));
  }

  protected canResize(column: ColumnDef): boolean {
    return this.resizable() && column.resizable !== false;
  }

  /** A stable measuring function per column, so the input identity holds. */
  protected measurerFor(key: string): () => number {
    let measurer = this.measurers.get(key);
    if (!measurer) {
      measurer = () => {
        const host = this.container()?.nativeElement;
        const cell = host?.querySelector<HTMLElement>(`[data-de-column="${key}"]`);
        return cell?.getBoundingClientRect().width ?? 0;
      };
      this.measurers.set(key, measurer);
    }
    return measurer;
  }

  protected columnTemplate(key: string) {
    return this.columnTemplates().find((t) => t.deColumn() === key)?.template;
  }

  protected ariaSort(column: ColumnDef): string {
    if (this.sort().key !== column.key) return 'none';
    return this.sort().direction === 'asc' ? 'ascending' : 'descending';
  }

  protected templateFor(item: DocumentItem): ItemTemplate | undefined {
    return item.type === 'folder' ? this.folderTemplate() : this.fileTemplate();
  }

  protected onClick(item: DocumentItem, event: MouseEvent): void {
    const behaviour = item.type === 'folder' ? this.interaction().folderClick : this.interaction().fileClick;
    const selectable = this.selectionEnabled() && this.canSelect(item);
    if (event.shiftKey && selectable) return this.selectRange.emit(item);
    if ((event.metaKey || event.ctrlKey) && selectable) return this.toggleSelect.emit(item);
    if (behaviour === 'open') return this.open.emit(item);
    if (behaviour === 'select' && this.selectionEnabled()) this.toggleSelect.emit(item);
  }

  /**
   * All of the decision lives here, including the shift-range the row would
   * otherwise handle — propagation stops here, so the row never sees the click.
   *
   * There is deliberately no `(change)` handler. `preventDefault()` does not
   * reliably suppress a checkbox's change event, so a toggle there would undo
   * the range the moment it was made. A keyboard Space also dispatches a click,
   * so nothing is lost by ignoring change entirely.
   */
  protected onCheckboxClick(event: MouseEvent, item: DocumentItem): void {
    event.stopPropagation();
    if (!this.canSelect(item)) return;
    if (event.shiftKey) this.selectRange.emit(item);
    else this.toggleSelect.emit(item);
  }

  protected onDoubleClick(item: DocumentItem): void {
    if (this.interaction().doubleClick === 'open') this.open.emit(item);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    this.roving.handleKey(event, this.items(), {
      onOpen: (item) => this.open.emit(item),
      onToggleSelect: (item) => this.toggleSelect.emit(item),
      onSelectRange: (item) => this.selectRange.emit(item),
      onSelectAll: () => this.selectAll.emit(),
      onNavigateUp: () => this.navigateUp.emit(),
      onClearSelection: () => this.clearSelection.emit(),
    });
  }
}
